import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { caseService } from '@/services/case-service'
import type { Case } from '@/types/case'
import type { CaseLink } from '@/lib/task-form'

interface CasePickerProps {
  value: CaseLink | null
  onChange: (value: CaseLink | null) => void
  disabled?: boolean
}

const toLink = (c: Case): CaseLink => ({
  id: c.id,
  title: c.title,
  number: c.courtNumber || c.processNumber || c.caseNumber,
  clientId: c.clientId,
  clientName: c.client?.name,
})

/** Nº do processo: busca o caso (o vínculo leva cliente junto). */
export function CasePicker({ value, onChange, disabled }: CasePickerProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<Case[]>([])

  useEffect(() => {
    if (query.trim().length < 3) {
      setResults([])
      return
    }
    let active = true
    const timer = setTimeout(() => {
      caseService.searchCases(query.trim())
        .then((found) => { if (active) setResults(found.slice(0, 8)) })
        .catch(() => { if (active) setResults([]) })
    }, 300)
    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [query])

  if (value) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm">
        <span className="min-w-0 truncate">
          <span className="tabular-nums">{value.number}</span>
          {value.title && <span className="text-muted-foreground"> · {value.title}</span>}
        </span>
        {!disabled && (
          <Button type="button" variant="ghost" size="icon" aria-label="Remover processo" onClick={() => onChange(null)}>
            <X className="size-4" />
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="relative">
      <Input
        id="task-processo"
        aria-label="Nº do processo"
        placeholder={disabled ? 'Sem processo vinculado' : 'Buscar processo pelo número ou título'}
        value={query}
        disabled={disabled}
        onChange={(e) => setQuery(e.target.value)}
      />
      {results.length > 0 && (
        <ul role="listbox" aria-label="Processos encontrados" className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover p-1 shadow-md">
          {results.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                role="option"
                aria-selected={false}
                className="w-full rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted"
                onClick={() => {
                  onChange(toLink(c))
                  setQuery('')
                  setResults([])
                }}
              >
                <span className="tabular-nums">{toLink(c).number}</span>
                <span className="text-muted-foreground"> · {c.title}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
